import React, { useState, useEffect, useRef } from 'react';
import {
  Card,
  Table,
  Input,
  Select,
  DatePicker,
  message,
  Empty,
  Typography,
  Button,
  Space,
  Image,
  Drawer,
  Spin
} from 'antd';
import { ExportOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import FilterPanel from '../../../components/common/FilterPanel';
import * as orderService from '../../../services/order/orderService';
import * as itemService from '../../../services/master-data/itemService';
import OrderDetailContent from '../order/components/OrderDetailContent';

const { RangePicker } = DatePicker;
const { Link } = Typography;

/**
 * 替换管理列表页
 * 路由: /order/substitute-management/list
 */
const SubstituteManagement = () => {
  const { t } = useTranslation();

  // 状态
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [dataSource, setDataSource] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  
  // 搜索表单
  const [searchParams, setSearchParams] = useState({
    orderNo: '',
    originalItemCode: undefined,
    substituteItemCode: undefined,
    pickerName: '',
    substituteTimeRange: null,
  });

  // 货品远程搜索
  const [originalItemOptions, setOriginalItemOptions] = useState([]);
  const [originalItemSearching, setOriginalItemSearching] = useState(false);
  const originalItemTimer = useRef();

  const [substituteItemOptions, setSubstituteItemOptions] = useState([]);
  const [substituteItemSearching, setSubstituteItemSearching] = useState(false);
  const substituteItemTimer = useRef();

  const handleItemSearch = (keyword, setOptions, setSearching, timerRef) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!keyword || keyword.length < 2) {
      setOptions([]);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await itemService.itemOption({ code: keyword });
        if (res.success && res.data) {
          setOptions(
            res.data.map((opt) => ({
              label: opt.label,
              value: opt.value,
            }))
          );
        }
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  // 获取列表数据（overrideParams 用于重置时直接传空条件，避免 stale state）
  const fetchData = async (page = 1, pageSize = 20, overrideParams) => {
    setLoading(true);
    try {
      const src = overrideParams || searchParams;
      const params = {
        currentPage: page,
        pageSize,
        orderNo: src.orderNo || undefined,
        originalItemCode: src.originalItemCode || undefined,
        substituteItemCode: src.substituteItemCode || undefined,
        pickerName: src.pickerName || undefined,
        substituteTimeStart: src.substituteTimeRange?.[0]?.valueOf(),
        substituteTimeEnd: src.substituteTimeRange?.[1]?.valueOf(),
      };

      const response = await orderService.pageSubstituteRecord(params);
      
      if (response.success) {
        setDataSource(response.data?.tableData || []);
        setPagination({
          current: response.data?.paging?.currentPage || 1,
          pageSize: response.data?.paging?.pageSize || 20,
          total: response.data?.paging?.totalCount || 0,
        });
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询替换记录列表失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载数据
  useEffect(() => {
    fetchData();
  }, []);

  // 搜索
  const handleSearch = () => {
    fetchData(1, pagination.pageSize);
  };

  // 重置
  const handleReset = () => {
    const emptyParams = {
      orderNo: '',
      originalItemCode: undefined,
      substituteItemCode: undefined,
      pickerName: '',
      substituteTimeRange: null,
    };
    setSearchParams(emptyParams);
    setOriginalItemOptions([]);
    setSubstituteItemOptions([]);
    fetchData(1, pagination.pageSize, emptyParams);
  };

  // 分页变化
  const handleTableChange = (paginationConfig) => {
    fetchData(paginationConfig.current, paginationConfig.pageSize);
  };

  // Drawer状态管理
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);

  const handleViewOrder = async (orderNo) => {
    setDrawerVisible(true);
    setDrawerLoading(true);
    try {
      const response = await orderService.getOrderByOrderNo(orderNo);
      if (response.success) {
        setCurrentOrder(response.data);
      } else {
        message.error(response.errorMessage || t('common.message.operationFailed'));
      }
    } catch (error) {
      console.error('查询订单详情失败:', error);
      message.error(t('common.message.operationFailed'));
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleCloseDrawer = () => {
    setDrawerVisible(false);
    setCurrentOrder(null);
  };

  // 导出
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const params = {
        orderNo: searchParams.orderNo || undefined,
        originalItemCode: searchParams.originalItemCode || undefined,
        substituteItemCode: searchParams.substituteItemCode || undefined,
        pickerName: searchParams.pickerName || undefined,
        substituteTimeStart: searchParams.substituteTimeRange?.[0]?.valueOf(),
        substituteTimeEnd: searchParams.substituteTimeRange?.[1]?.valueOf(),
      };
      message.loading(t('common.message.exportingData') || 'Exporting...', 0);
      const response = await orderService.exportSubstituteRecord(params);
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data.downloadUrl;
        link.download = response.data.fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.destroy();
        message.success(t('order.substituteManagement.list.export.success'));
      } else {
        message.destroy();
        message.error(response.errorMessage || t('order.substituteManagement.list.export.fail'));
      }
    } catch (error) {
      message.destroy();
      const errorMsg = error?.response?.data?.errorMessage || t('order.substituteManagement.list.export.fail');
      message.error(errorMsg);
    } finally {
      setExportLoading(false);
    }
  };

  // 表格列定义
  //策略：仅 orderNo 左固定 + 显式 width（主键，必须左固定方便识别）；
  //其余列不写 width，由 AntD 按"表头文案 + 单元格内容"中最宽者自适应；
  //配合 ellipsis: true 防止极长内容溢出；scroll.x: 'max-content' 让容器不够时自动出横向滚动条，彻底兼容泰语等长文案场景。
  const columns = [
    {
      title: t('order.substituteManagement.list.table.orderNo'),
      dataIndex: 'orderNo',
      key: 'orderNo',
      width: 180,
      fixed: 'left',
      render: (orderNo) => (
        <Link onClick={() => handleViewOrder(orderNo)}>
          {orderNo}
        </Link>
      ),
    },
    {
      title: t('order.substituteManagement.list.table.originalItemCode'),
      dataIndex: 'originalItemCode',
      key: 'originalItemCode',
      ellipsis: true
    },
    {
      title: t('order.substituteManagement.list.table.originalItemName'),
      dataIndex: 'originalItemName',
      key: 'originalItemName',
      ellipsis: true
    },
    {
      title: 'Image',
      dataIndex: 'originalImageUrl',
      key: 'originalImageUrl',
      width: 80,
      render: (url) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: t('order.substituteManagement.list.table.substituteItemCode'),
      dataIndex: 'substituteItemCode',
      key: 'substituteItemCode',
      ellipsis: true
    },
    {
      title: t('order.substituteManagement.list.table.substituteItemName'),
      dataIndex: 'substituteItemName',
      key: 'substituteItemName',
      ellipsis: true
    },
    {
      title: 'Image',
      dataIndex: 'substituteImageUrl',
      key: 'substituteImageUrl',
      width: 80,
      render: (url) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: t('order.substituteManagement.list.table.originalQty'),
      dataIndex: 'originalQty',
      key: 'originalQty',
      align: 'right',
      ellipsis: true
    },
    {
      title: t('order.substituteManagement.list.table.originalPrice'),
      dataIndex: 'originalPrice',
      key: 'originalPrice',
      align: 'right',
      ellipsis: true,
      render: (val) => `฿${val?.toFixed(3) || '0.000'}`
    },
    {
      title: t('order.substituteManagement.list.table.substituteQty'),
      dataIndex: 'substituteQty',
      key: 'substituteQty',
      align: 'right',
      ellipsis: true
    },
    {
      title: t('order.substituteManagement.list.table.substitutePrice'),
      dataIndex: 'substitutePrice',
      key: 'substitutePrice',
      align: 'right',
      ellipsis: true,
      render: (val) => `฿${val?.toFixed(3) || '0.000'}`
    },
    {
      title: t('order.substituteManagement.list.table.priceDiff'),
      dataIndex: 'priceDiff',
      key: 'priceDiff',
      align: 'right',
      ellipsis: true,
      render: (diff) => (
        <span style={{ color: diff < 0 ? '#ff4d4f' : '#52c41a' }}>
          ฿{diff?.toFixed(3) || '0.000'}
        </span>
      )
    },

    {
      title: t('order.substituteManagement.list.table.pickerName'),
      dataIndex: 'pickerName',
      key: 'pickerName',
      ellipsis: true
    },
    {
      title: t('order.substituteManagement.list.table.substituteTime'),
      dataIndex: 'substituteTime',
      key: 'substituteTime',
      ellipsis: true,
      render: (time) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-'
    },
  ];

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 搜索区域 */}
      <FilterPanel
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '16px' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              {t('order.substituteManagement.list.search.orderNo')}
            </label>
            <Input
              placeholder={t('order.substituteManagement.list.search.placeholder.orderNo')}
              value={searchParams.orderNo}
              onChange={(e) => setSearchParams({ ...searchParams, orderNo: e.target.value })}
              allowClear
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              {t('order.substituteManagement.list.search.originalItemCode')}
            </label>
            <Select
              showSearch
              allowClear
              placeholder={t('order.substituteManagement.list.search.placeholder.originalItemCode')}
              value={searchParams.originalItemCode}
              filterOption={false}
              onSearch={(keyword) => handleItemSearch(keyword, setOriginalItemOptions, setOriginalItemSearching, originalItemTimer)}
              onChange={(value) => setSearchParams({ ...searchParams, originalItemCode: value })}
              notFoundContent={originalItemSearching ? <Spin size="small" /> : null}
              options={originalItemOptions}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              {t('order.substituteManagement.list.search.substituteItemCode')}
            </label>
            <Select
              showSearch
              allowClear
              placeholder={t('order.substituteManagement.list.search.placeholder.substituteItemCode')}
              value={searchParams.substituteItemCode}
              filterOption={false}
              onSearch={(keyword) => handleItemSearch(keyword, setSubstituteItemOptions, setSubstituteItemSearching, substituteItemTimer)}
              onChange={(value) => setSearchParams({ ...searchParams, substituteItemCode: value })}
              notFoundContent={substituteItemSearching ? <Spin size="small" /> : null}
              options={substituteItemOptions}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              {t('order.substituteManagement.list.search.pickerName')}
            </label>
            <Input
              placeholder={t('order.substituteManagement.list.search.placeholder.pickerName')}
              value={searchParams.pickerName}
              onChange={(e) => setSearchParams({ ...searchParams, pickerName: e.target.value })}
              allowClear
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: '8px' }}>
              {t('order.substituteManagement.list.search.substituteTime')}
            </label>
            <RangePicker
              style={{ width: '100%' }}
              value={searchParams.substituteTimeRange}
              onChange={(dates) => setSearchParams({ ...searchParams, substituteTimeRange: dates })}
              showTime
            />
          </div>
        </div>
      </FilterPanel>

      {/* 表格区域 */}
      <Card 
        style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column', marginTop: '16px' }}
        bodyStyle={{ flex: 1, overflow: 'hidden', padding: 0 }}
        extra={
          <Button
            icon={exportLoading ? <LoadingOutlined /> : <ExportOutlined />}
            onClick={handleExport}
            loading={exportLoading}
          >
            {t('common.export')}
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={dataSource}
          rowKey="id"
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => t('common.pagination.total', { total }),
          }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content', y: 'calc(100vh - 420px)' }}
          locale={{
            emptyText: <Empty description={t('common.message.noData')} />,
          }}
        />
      </Card>

      {/* 订单详情Drawer */}
      <Drawer
        title={t('order.order.detail.title')}
        placement="right"
        width={960}
        onClose={handleCloseDrawer}
        open={drawerVisible}
        destroyOnClose
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={handleCloseDrawer}>
              {t('common.close')}
            </Button>
          </div>
        }
      >
        <Spin spinning={drawerLoading}>
          {currentOrder && <OrderDetailContent orderDetail={currentOrder} />}
        </Spin>
      </Drawer>
    </div>
  );
};

export default SubstituteManagement;
